import { useState, type ReactNode } from 'react'
import { Link, useParams } from 'react-router'
import { ArrowRight, Building2, Check, FolderKanban } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { ErrorState, LoadingState } from '@/components/composites/states'
import { cn } from '@/lib/utils'
import { paths } from '@/routes/paths'
import { crm2Api, useCrm2Deal, useCrm2Meta, useCrm2Write, type Brief, type DealDetail, type DealPatch } from './api'
import { Timeline } from './Timeline'
import { dateInput, Field, fieldClass, fromDateInput, LEVEL_SOFT, LINK_CHIP, money, PersonSelect, PHONE_HINT, ROLE_LABEL, todayPlus, usePeopleNames } from './shared'

/** One deal: where it is, what the next stage needs, the hand-overs, its people and its history. */
export function DealSheet({ id, onClose, onOpen }: { id: string | null; onClose: () => void; onOpen: (id: string) => void }) {
  const query = useCrm2Deal(id)
  return (
    <Sheet open={Boolean(id)} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:w-[75vw] sm:max-w-none">
        {query.isPending ? (
          <LoadingState label="Loading the deal" />
        ) : query.error ? (
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        ) : (
          // Keyed on the version so every form below starts from what was just saved.
          <DealBody key={`${query.data.deal.id}:${query.data.deal.version}`} d={query.data} onOpen={onOpen} />
        )}
      </SheetContent>
    </Sheet>
  )
}

function DealBody({ d, onOpen }: { d: DealDetail; onOpen: (id: string) => void }) {
  const { workspaceSlug = '' } = useParams()
  const meta = useCrm2Meta().data
  const name = usePeopleNames()
  const deal = d.deal
  const open = deal.status === 'open'
  const groups = meta?.pipelines.find((p) => p.key === deal.pipeline)?.groups ?? []
  const current = groups.find((g) => g.key === deal.group)
  const pipelineLabel = meta?.pipelines.find((p) => p.key === deal.pipeline)?.label ?? deal.pipeline
  const move = useCrm2Write(({ stage }: { stage: string }) => crm2Api.move(deal.id, stage), (r) => `Moved to ${r.stageLabel}`)
  const reopen = useCrm2Write(() => crm2Api.reopen(deal.id), () => 'Reopened')
  const [panel, setPanel] = useState<'handoff' | 'win' | 'lose' | null>(null)

  return (
    <div className="grid gap-5 pb-8">
      <SheetHeader className="space-y-1 text-left">
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <span className="rounded-full bg-accent px-2 py-0.5 font-medium text-foreground">
            {pipelineLabel} · {deal.groupLabel}
            {deal.statusName ? ` · ${deal.statusName}` : ''}
          </span>
          {deal.status !== 'open' ? <span className={cn('rounded-full px-2 py-0.5 font-medium', deal.status === 'won' ? 'bg-success-soft text-success' : 'bg-muted text-text-secondary')}>{deal.status === 'won' ? 'Won' : `Lost · ${deal.lostReason ?? ''}`}</span> : null}
          <span>· {deal.fresh.daysInStage} days in stage</span>
          {deal.value > 0 ? <span className="num font-semibold text-foreground">· {money(deal.value)}</span> : null}
        </div>
        <SheetTitle className="text-xl">
          <Link to={paths.crm2Account(workspaceSlug, deal.accountId)} className={LINK_CHIP}>
            {deal.accountName}
          </Link>
        </SheetTitle>
        <SheetDescription>
          {deal.name !== deal.accountName ? `${deal.name} · ` : ''}Owner {name(deal.ownerId)}
          {deal.lostReason && deal.stage === 'nurture' ? ` · back in Nurture: ${deal.lostReason}` : ''}
        </SheetDescription>
        {deal.fresh.reasons.length ? (
          <div className="flex flex-wrap gap-1.5 pt-1">
            {deal.fresh.reasons.map((r) => (
              <span key={r} className={cn('rounded px-1.5 py-0.5 text-xs', LEVEL_SOFT[deal.fresh.level])}>{r}</span>
            ))}
          </div>
        ) : null}
        <div className="flex flex-wrap gap-2 pt-2">
          {d.project ? (
            <Button asChild size="sm" variant="outline">
              <Link to={paths.pm2Project(workspaceSlug, d.project.id)}>
                <FolderKanban className="size-4" />
                Open TapPM · {d.project.phaseLabel}
                {d.project.cycle > 1 ? ` (cycle ${d.project.cycle})` : ''}
                {d.project.status !== 'active' ? ` · ${d.project.status}` : ''}
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          ) : null}
          <Button asChild size="sm" variant="outline">
            <Link to={paths.crm2Account(workspaceSlug, deal.accountId)}>
              <Building2 className="size-4" />
              Open account
              <ArrowRight className="size-4" />
            </Link>
          </Button>
        </div>
      </SheetHeader>

      {/* The stages, each saying what it still needs. */}
      <section className="grid gap-2">
        <div className="flex flex-wrap items-center gap-1">
          {groups.map((g) => {
            const here = g.key === deal.group
            const missing = d.gates[g.statuses[0]!.key] ?? []
            return (
              <button
                key={g.key}
                type="button"
                disabled={!open || here || move.isPending}
                onClick={() => move.mutate({ stage: g.statuses[0]!.key })}
                title={missing.length ? `Needs: ${missing.join(', ')}` : g.rule}
                className={cn('rounded-md border px-2.5 py-1 text-xs', here ? 'border-brand bg-accent font-semibold' : 'border-border text-muted-foreground hover:bg-accent disabled:opacity-60', missing.length && !here && 'border-dashed')}
              >
                {g.label}
              </button>
            )
          })}
          {/* A column with more than one status (Demo: Booked or Done; Post-Pilot Review: reviewing or the contract) picks it here. */}
          {current && current.statuses.length > 1 ? (
            <label className="ml-2 inline-flex items-center gap-1.5 text-xs text-muted-foreground">
              {current.label} status
              <select
                aria-label={`${current.label} status`}
                value={deal.stage}
                disabled={!open || move.isPending}
                onChange={(e) => move.mutate({ stage: e.target.value })}
                className="h-7 rounded-md border border-input bg-background px-2 text-xs font-medium text-foreground"
              >
                {current.statuses.map((st) => (
                  <option key={st.key} value={st.key}>
                    {st.label}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
        </div>
        {open ? <NextNeeds d={d} /> : null}
        <div className="flex flex-wrap gap-2">
          {open && deal.pipeline === 'marketing' && deal.stage === 'demo_done' ? (
            <Button size="sm" onClick={() => setPanel(panel === 'handoff' ? null : 'handoff')}>
              Hand over to Sales<ArrowRight className="size-4" />
            </Button>
          ) : null}
          {open && deal.pipeline === 'sales' && deal.group === 'post_pilot_review' ? (
            <Button size="sm" onClick={() => setPanel(panel === 'win' ? null : 'win')}>
              <Check className="size-4" />Contract signed
            </Button>
          ) : null}
          {open && deal.pipeline !== 'post_sales' ? (
            <Button size="sm" variant="outline" onClick={() => setPanel(panel === 'lose' ? null : 'lose')}>
              {deal.pipeline === 'partnership' || deal.pipeline === 'podcast' ? 'Close' : 'Lost or not now'}
            </Button>
          ) : null}
          {deal.status === 'lost' ? (
            <Button size="sm" variant="outline" onClick={() => reopen.mutate(undefined)} disabled={reopen.isPending}>
              Reopen
            </Button>
          ) : null}
        </div>
        {panel === 'handoff' ? <HandoffForm d={d} onDone={() => setPanel(null)} /> : null}
        {panel === 'win' ? <WinForm d={d} onDone={(postId) => { setPanel(null); onOpen(postId) }} /> : null}
        {panel === 'lose' ? <LoseForm d={d} onDone={() => setPanel(null)} /> : null}
      </section>

      <DetailsForm d={d} />
      <Contacts d={d} />
      <Timeline accountId={deal.accountId} dealId={deal.id} items={d.timeline} contacts={d.contacts} />
      <ChangeLog d={d} />
    </div>
  )
}

/** The next stage's missing facts, said before a click is refused. */
function NextNeeds({ d }: { d: DealDetail }) {
  const meta = useCrm2Meta().data
  const groups = meta?.pipelines.find((p) => p.key === d.deal.pipeline)?.groups ?? []
  const g = groups.find((x) => x.key === d.deal.group)
  // Inside a column with statuses, the next status comes first (Booked → Done); then the next column.
  const at = g ? g.statuses.findIndex((x) => x.key === d.deal.stage) : -1
  const next = g && at >= 0 && at < g.statuses.length - 1 ? { key: g.statuses[at + 1]!.key, label: `${g.label} · ${g.statuses[at + 1]!.label}`, rule: g.rule } : (() => {
    const n = groups[groups.findIndex((x) => x.key === d.deal.group) + 1]
    return n ? { key: n.statuses[0]!.key, label: n.label, rule: n.rule } : null
  })()
  if (!next) return null
  const missing = d.gates[next.key] ?? []
  return (
    <p className="text-xs text-muted-foreground">
      {missing.length ? (
        <>
          To move to <span className="font-medium text-foreground">{next.label}</span>: {missing.join(', ')}. Fill them in under Details, save, then move it.
        </>
      ) : (
        <>
          Ready for <span className="font-medium text-foreground">{next.label}</span>. {next.rule}.
        </>
      )}
    </p>
  )
}

function Panel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="grid gap-3 rounded-xl border border-brand/40 bg-accent/40 p-3">
      <h3 className="text-sm font-semibold">{title}</h3>
      {children}
    </div>
  )
}

function HandoffForm({ d, onDone }: { d: DealDetail; onDone: () => void }) {
  const meta = useCrm2Meta().data
  const [ownerId, setOwnerId] = useState<string | null>(null)
  const [value, setValue] = useState(d.deal.value ? String(d.deal.value) : '')
  const [nextStep, setNextStep] = useState('Pre-Pilot call')
  const [nextStepAt, setNextStepAt] = useState(() => todayPlus(2))
  const [products, setProducts] = useState(d.deal.products)
  const go = useCrm2Write(() => crm2Api.handoff(d.deal.id, { ownerId: ownerId!, value: Number(value), nextStep, nextStepAt: fromDateInput(nextStepAt)!, products }), () => 'Handed over to Sales. It is now open in TapPM.')
  return (
    <Panel title="Hand over to Sales · Pre-Pilot">
      <p className="text-xs text-muted-foreground">Sales and AM take it from here. The deal moves to Pre-Pilot and opens in TapPM, with the pilot set-up tasks on tech.</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Sales owner">
          <PersonSelect label="Sales owner" value={ownerId} onChange={setOwnerId} allowNone={false} />
        </Field>
        <Field label="Deal value (USD)">
          <Input type="number" min={1} value={value} onChange={(e) => setValue(e.target.value)} />
        </Field>
        <Field label="Next step">
          <Input value={nextStep} onChange={(e) => setNextStep(e.target.value)} />
        </Field>
        <Field label="By">
          <Input type="date" value={nextStepAt} onChange={(e) => setNextStepAt(e.target.value)} />
        </Field>
      </div>
      <ProductPicker value={products} onChange={setProducts} options={meta?.products ?? []} />
      <div>
        <Button size="sm" onClick={() => go.mutate(undefined, { onSuccess: onDone })} disabled={go.isPending || !ownerId || !(Number(value) > 0) || !nextStep.trim() || !nextStepAt}>
          Hand over
        </Button>
      </div>
    </Panel>
  )
}

function WinForm({ d, onDone }: { d: DealDetail; onDone: (postSalesId: string) => void }) {
  const [renewalAt, setRenewalAt] = useState(() => todayPlus(365))
  const [ownerId, setOwnerId] = useState<string | null>(d.deal.ownerId)
  const go = useCrm2Write(() => crm2Api.win(d.deal.id, { renewalAt: fromDateInput(renewalAt), ownerId }), () => 'Won. Post-sales is open in Implementation.')
  return (
    <Panel title="Contract signed">
      <p className="text-xs text-muted-foreground">The deal is marked won, a Post-sales deal opens in Implementation and TapPM moves with it. A renewal task lands 90 days before the renewal date.</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Account manager">
          <PersonSelect label="Account manager" value={ownerId} onChange={setOwnerId} />
        </Field>
        <Field label="Renews on">
          <Input type="date" value={renewalAt} onChange={(e) => setRenewalAt(e.target.value)} />
        </Field>
      </div>
      <div>
        <Button size="sm" onClick={() => go.mutate(undefined, { onSuccess: (r) => onDone(r.postSales.id) })} disabled={go.isPending}>
          Mark won
        </Button>
      </div>
    </Panel>
  )
}

function LoseForm({ d, onDone }: { d: DealDetail; onDone: () => void }) {
  const meta = useCrm2Meta().data
  const standalone = d.deal.pipeline === 'partnership' || d.deal.pipeline === 'podcast'
  const [reason, setReason] = useState('')
  const [note, setNote] = useState('')
  const [nurture, setNurture] = useState(!standalone)
  const [until, setUntil] = useState(() => todayPlus(60))
  const go = useCrm2Write(() => crm2Api.lose(d.deal.id, { reason, note: note.trim() || null, nurtureUntil: nurture && reason !== 'No potential' ? fromDateInput(until) : null }), (r) => (r.status === 'open' ? 'Back in Nurture' : 'Closed'))
  return (
    <Panel title={standalone ? 'Close this' : 'Lost, or not now'}>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Reason">
          <select className={fieldClass} value={reason} onChange={(e) => setReason(e.target.value)}>
            <option value="" disabled>Pick a reason</option>
            {meta?.lostReasons.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </Field>
        {!standalone && reason !== 'No potential' ? (
          <Field label="What happens to it">
            <select className={fieldClass} value={nurture ? 'nurture' : 'lost'} onChange={(e) => setNurture(e.target.value === 'nurture')}>
              <option value="nurture">Back to Nurture, check in later</option>
              <option value="lost">Closed as lost</option>
            </select>
          </Field>
        ) : null}
        {!standalone && nurture && reason !== 'No potential' ? (
          <Field label="Check in on">
            <Input type="date" value={until} onChange={(e) => setUntil(e.target.value)} />
          </Field>
        ) : null}
        <Field label="Note">
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="What happened" />
        </Field>
      </div>
      {reason === 'No potential' ? <p className="text-xs text-muted-foreground">No potential closes the lead for good: it will not come back through Nurture.</p> : null}
      <div>
        <Button size="sm" variant="outline" onClick={() => go.mutate(undefined, { onSuccess: onDone })} disabled={go.isPending || !reason}>
          Save
        </Button>
      </div>
    </Panel>
  )
}

function ProductPicker({ value, onChange, options }: { value: string[]; onChange: (v: string[]) => void; options: Array<{ key: string; label: string }> }) {
  return (
    <div className="flex flex-wrap gap-1.5" role="group" aria-label="Products">
      {options.map((p) => {
        const on = value.includes(p.key)
        return (
          <button key={p.key} type="button" aria-pressed={on} onClick={() => onChange(on ? value.filter((x) => x !== p.key) : [...value, p.key])} className={cn('rounded-full border px-2.5 py-0.5 text-xs', on ? 'border-brand bg-accent font-medium' : 'border-border text-muted-foreground hover:bg-accent')}>
            {p.label}
          </button>
        )
      })}
    </div>
  )
}

function DetailsForm({ d }: { d: DealDetail }) {
  const meta = useCrm2Meta().data
  const deal = d.deal
  const p = deal.pipeline
  const [f, setF] = useState({
    name: deal.name,
    value: deal.value ? String(deal.value) : '',
    ownerId: deal.ownerId,
    source: deal.source ?? '',
    nextStep: deal.nextStep ?? '',
    nextStepAt: dateInput(deal.nextStepAt),
    checkInAt: dateInput(deal.checkInAt),
    demoBy: deal.demoBy,
    pilotStartAt: dateInput(deal.pilotStartAt),
    pilotEndAt: dateInput(deal.pilotEndAt),
    decisionAt: dateInput(deal.decisionAt),
    successCriteria: deal.successCriteria ?? '',
    products: deal.products,
    problem: d.brief.problem ?? '',
    requirements: d.brief.requirements ?? '',
    scope: d.brief.scope ?? '',
    aimStrategy: d.brief.aimStrategy ?? '',
  })
  // The fields and the brief save together; the brief only when one of its four changed.
  const save = useCrm2Write(
    async ({ body, brief }: { body: DealPatch; brief: Brief | null }) => {
      const saved = await crm2Api.patchDeal(deal.id, body)
      if (brief) await crm2Api.saveBrief(deal.id, brief)
      return saved
    },
    () => 'Saved',
  )
  const editable = deal.status === 'open'
  const briefNow: Brief = { problem: f.problem.trim() || null, requirements: f.requirements.trim() || null, scope: f.scope.trim() || null, aimStrategy: f.aimStrategy.trim() || null }
  const briefChanged = (Object.keys(briefNow) as Array<keyof Brief>).some((k) => (briefNow[k] ?? null) !== (d.brief[k] ?? null))
  const submit = () =>
    save.mutate({ brief: briefChanged ? briefNow : null, body: {
      name: f.name.trim() || deal.name,
      ...(p !== 'marketing' && p !== 'podcast' ? { value: Number(f.value) || 0 } : {}),
      ownerId: f.ownerId,
      source: f.source.trim() || null,
      nextStep: f.nextStep.trim() || null,
      nextStepAt: fromDateInput(f.nextStepAt),
      checkInAt: fromDateInput(f.checkInAt),
      demoBy: f.demoBy,
      pilotStartAt: fromDateInput(f.pilotStartAt),
      pilotEndAt: fromDateInput(f.pilotEndAt),
      decisionAt: fromDateInput(f.decisionAt),
      successCriteria: f.successCriteria.trim() || null,
      products: f.products,
    } })
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF({ ...f, [k]: v })
  return (
    <section className="grid gap-3 rounded-xl border border-border bg-card p-4 shadow-sm">
      <h3 className="text-sm font-semibold">Details</h3>
      <fieldset disabled={!editable} className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Field label="Deal name">
          <Input value={f.name} onChange={(e) => set('name', e.target.value)} />
        </Field>
        <Field label="Owner">
          <PersonSelect label="Owner" value={f.ownerId} onChange={(v) => set('ownerId', v)} />
        </Field>
        {deal.stage === 'nurture' ? (
          <Field label="Check in on">
            <Input type="date" value={f.checkInAt} onChange={(e) => set('checkInAt', e.target.value)} />
          </Field>
        ) : null}
        <Field label="Next step" hint={deal.stage === 'nurture' ? 'Needed to move it out of Nurture.' : undefined}>
          <Input value={f.nextStep} onChange={(e) => set('nextStep', e.target.value)} />
        </Field>
        <Field label="By">
          <Input type="date" value={f.nextStepAt} onChange={(e) => set('nextStepAt', e.target.value)} />
        </Field>
        {p !== 'marketing' && p !== 'podcast' ? (
          <Field label="Value (USD)">
            <Input type="number" min={0} value={f.value} onChange={(e) => set('value', e.target.value)} />
          </Field>
        ) : null}
        {p === 'marketing' ? (
          <Field label="Demo by">
            <PersonSelect label="Demo by" value={f.demoBy} onChange={(v) => set('demoBy', v)} />
          </Field>
        ) : null}
        <Field label="Source">
          <Input value={f.source} onChange={(e) => set('source', e.target.value)} />
        </Field>
        {p === 'sales' ? (
          <>
            <Field label="Pilot starts">
              <Input type="date" value={f.pilotStartAt} onChange={(e) => set('pilotStartAt', e.target.value)} />
            </Field>
            <Field label="Pilot ends">
              <Input type="date" value={f.pilotEndAt} onChange={(e) => set('pilotEndAt', e.target.value)} />
            </Field>
            <Field label="Decision expected">
              <Input type="date" value={f.decisionAt} onChange={(e) => set('decisionAt', e.target.value)} />
            </Field>
            <div className="col-span-full">
              <Field label="What success looks like">
                <Textarea rows={2} value={f.successCriteria} onChange={(e) => set('successCriteria', e.target.value)} />
              </Field>
            </div>
          </>
        ) : null}
        <div className="col-span-full grid gap-3 border-t border-border-soft pt-3 lg:grid-cols-2">
          <Field label="Problem" hint="What the client is trying to fix, in their words.">
            <Textarea rows={3} value={f.problem} onChange={(e) => set('problem', e.target.value)} />
          </Field>
          <Field label="Requirements" hint="What it has to do for them to say yes.">
            <Textarea rows={3} value={f.requirements} onChange={(e) => set('requirements', e.target.value)} />
          </Field>
          <Field label="Scope" hint="What is in, and what is not.">
            <Textarea rows={3} value={f.scope} onChange={(e) => set('scope', e.target.value)} />
          </Field>
          <Field label="Aim & Strategy" hint="What we want from this deal and how we get there.">
            <Textarea rows={3} value={f.aimStrategy} onChange={(e) => set('aimStrategy', e.target.value)} />
          </Field>
        </div>
        {p !== 'partnership' && p !== 'podcast' ? (
          <div className="col-span-full">
            <ProductPicker value={f.products} onChange={(v) => set('products', v)} options={meta?.products ?? []} />
          </div>
        ) : null}
      </fieldset>
      {editable ? (
        <div>
          <Button size="sm" variant="outline" onClick={submit} disabled={save.isPending}>
            Save details
          </Button>
        </div>
      ) : null}
    </section>
  )
}

function Contacts({ d }: { d: DealDetail }) {
  const { workspaceSlug = '' } = useParams()
  const meta = useCrm2Meta().data
  const role = useCrm2Write(({ contactId, value }: { contactId: string; value: string | null }) => crm2Api.setRole(d.deal.id, contactId, value))
  const add = useCrm2Write((body: { name: string; title: string | null; email: string | null; phone: string | null; role: string }) => crm2Api.addDealContact(d.deal.id, body), () => 'Contact added')
  const [f, setF] = useState({ name: '', title: '', email: '', phone: '', role: 'user' })
  const onDeal = d.contacts.filter((c) => c.role)
  const others = d.contacts.filter((c) => !c.role)
  return (
    <section className="grid gap-3 rounded-xl border border-border bg-card p-4 shadow-sm">
      <h3 className="text-sm font-semibold">People</h3>
      <ul className="grid gap-2">
        {onDeal.map((c) => (
          <li key={c.id} className="flex flex-wrap items-center gap-2 text-sm">
            <Link to={paths.crm2Contact(workspaceSlug, c.id)} className={cn(LINK_CHIP, 'text-sm')}>{c.name}</Link>
            <span className="text-muted-foreground">{[c.title, c.email, c.phone].filter(Boolean).join(' · ')}</span>
            <Button asChild size="sm" variant="outline" className="h-7 px-2 text-xs">
              <Link to={paths.crm2Contact(workspaceSlug, c.id)}>
                History<ArrowRight className="size-3.5" />
              </Link>
            </Button>
            <select aria-label={`${c.name}'s role`} value={c.role ?? ''} onChange={(e) => role.mutate({ contactId: c.id, value: e.target.value || null })} className="ml-auto h-8 rounded-md border border-input bg-background px-2 text-xs">
              {meta?.contactRoles.map((r) => <option key={r} value={r}>{ROLE_LABEL[r] ?? r}</option>)}
              <option value="">Take off this deal</option>
            </select>
          </li>
        ))}
        {!onDeal.length ? <li className="text-sm text-muted-foreground">Nobody on this deal yet.</li> : null}
      </ul>
      {others.length ? (
        <p className="text-xs text-muted-foreground">
          Also at {d.account?.name}:{' '}
          {others.map((c, i) => (
            <button key={c.id} type="button" className={LINK_CHIP} onClick={() => role.mutate({ contactId: c.id, value: 'user' })}>
              {c.name}
              {i < others.length - 1 ? ', ' : ''}
            </button>
          ))}{' '}
          (click to add)
        </p>
      ) : null}
      <div className="grid gap-2 sm:grid-cols-6">
        <Input aria-label="Name" placeholder="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        <Input aria-label="Title" placeholder="Title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
        <Input aria-label="Email" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
        <Input aria-label="Contact number" type="tel" placeholder="Contact number" title={PHONE_HINT} value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} />
        <select aria-label="Role" className={fieldClass} value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })}>
          {meta?.contactRoles.map((r) => <option key={r} value={r}>{ROLE_LABEL[r] ?? r}</option>)}
        </select>
        <Button size="sm" variant="outline" disabled={!f.name.trim() || add.isPending} onClick={() => add.mutate({ name: f.name.trim(), title: f.title.trim() || null, email: f.email.trim() || null, phone: f.phone.trim() || null, role: f.role }, { onSuccess: () => setF({ name: '', title: '', email: '', phone: '', role: 'user' }) })}>
          Add person
        </Button>
      </div>
    </section>
  )
}

/** Everything that changed on this deal, newest first: fields, people, the brief and stage moves. */
function ChangeLog({ d }: { d: DealDetail }) {
  const name = usePeopleNames()
  return (
    <section className="grid gap-2 rounded-xl border border-border bg-card p-4 shadow-sm">
      <h3 className="text-sm font-semibold">Change log</h3>
      <ol className="grid max-h-96 gap-1.5 overflow-y-auto text-sm">
        {d.changeLog.map((l) => (
          <li key={l.id} className="grid">
            <span className={cn(l.kind === 'stage' && 'font-medium')}>{l.text}</span>
            <span className="text-tiny text-text-muted">
              {new Date(l.at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })} · {l.by ? name(l.by) : 'Tapwork'}
            </span>
          </li>
        ))}
        {!d.changeLog.length ? <li className="text-muted-foreground">Nothing changed yet.</li> : null}
      </ol>
    </section>
  )
}
